import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CompleteSearchBar } from '@homeexchange/search';
import { usePromise } from '@homeexchange/shared';
import { searchProcessing } from '../../actions/search';
import Api from '../../api/Api';
import i18n from '../../i18n';
import { transformSearchesToRecentSearches } from '../../utils/transformSearchToRecentSearch';
import Feature from '../../models/Feature';
import FiltersModel from '../../models/Filters';

const fetchRecentSearches = () => Api.Search.getLastResearches().then((searches) => searches ?? []);

const isOnSearchPage = Boolean(window.location.pathname.match('/search-v2'));

const surroundingTags = Feature.FEATURES.filter((f) => f.category === Feature.SURROUNDING_TAGS).map(
    (f) => f.icon
);

const getSearchBarDefaultValue = (reduxFilters, reduxQueryString) => {
    if (!isOnSearchPage) return undefined;

    const params = new URLSearchParams(window.location.search);
    const pathMatch = window.location.pathname.match('/search-v2/(.+)');
    const defaultValue = {};

    const reduxLocation = reduxFilters?.location;
    if (reduxLocation?.bounds && !reduxLocation?.polygon?.location_id) {
        defaultValue.location = {
            id: 'bounds',
            value: i18n.t('search:selected_geographic_area'),
            bounds: reduxLocation.bounds
        };
    } else if (reduxLocation?.polygon?.location_id) {
        const slug = reduxQueryString || (pathMatch?.[1] ? decodeURIComponent(pathMatch[1]) : null);
        if (slug && slug !== 'everywhere') {
            defaultValue.location = {
                id: reduxLocation.polygon.location_id,
                value: slug,
                ...(reduxLocation.polygon.layer && { layer: reduxLocation.polygon.layer }),
                ...(reduxLocation.city && { city: reduxLocation.city }),
                ...(reduxLocation.country_code && { country_code: reduxLocation.country_code })
            };
        }
    } else {
        const locationId = params.get('location_id');
        const bounds = params.get('bounds');
        if (bounds) {
            const parsedBounds = FiltersModel.formatBounds(bounds);
            defaultValue.location = {
                id: 'bounds',
                value: i18n.t('search:selected_geographic_area'),
                ...(parsedBounds ? { bounds: parsedBounds } : {})
            };
        } else if (locationId && pathMatch?.[1]) {
            const locationValue = decodeURIComponent(pathMatch[1]);
            if (locationValue !== 'everywhere') {
                const layer = params.get('layer');
                const city = params.get('city');
                const countryCode = params.get('country_code');
                defaultValue.location = {
                    id: locationId,
                    value: locationValue,
                    ...(layer && { layer }),
                    ...(city && { city }),
                    ...(countryCode && { country_code: countryCode })
                };
            }
        }
    }

    // Dates
    const dateRanges = params.get('date_ranges');
    const lastMinute = params.get('last_minute');
    const durationKind = params.get('duration_kind');
    const flexibility = params.get('flexibility');

    if (dateRanges && !durationKind) {
        const [from, to] = dateRanges.split('_');
        if (from && to) {
            const flexMap = { 1: '1day', 3: '3days', 7: '7days', 30: '30days' };
            defaultValue.dateResult = {
                activeTabIndex: 0,
                dateRange: { start: new Date(from), end: new Date(to) },
                flexibility: flexibility ? flexMap[flexibility] || 'exact' : 'exact'
            };
        }
    } else if (dateRanges && durationKind) {
        const today = new Date();
        defaultValue.dateResult = {
            activeTabIndex: 1,
            flexibleDuration: durationKind,
            selectedMonths: dateRanges.split('--').map((m) => {
                const [y, mo] = m.split('-').map((v) => parseInt(v, 10));
                const lastDay = new Date(y, mo, 0).getDate();
                return new Date(y, mo - 1, Math.min(today.getDate(), lastDay));
            })
        };
    } else if (lastMinute === 'true') {
        defaultValue.dateResult = { activeTabIndex: 2 };
    }

    // Guests
    const adults = parseInt(params.get('adults'), 10) || 0;
    const children = parseInt(params.get('children'), 10) || 0;
    const babies = parseInt(params.get('babies'), 10) || 0;
    const filters = params.getAll('filter[]');
    if (adults || children || babies) {
        defaultValue.guests = {
            adults,
            children,
            babies,
            hasPets: filters.includes('pets-welcome')
        };
    }

    return Object.keys(defaultValue).length > 0 ? defaultValue : undefined;
};

const NavSearchBar = () => {
    const dispatch = useDispatch();
    const userId = useSelector((state) => state.user?.get('id'));
    const locale = i18n.language;
    const homes = useSelector((state) => state.homes);
    const currentFilters = useSelector((state) => state.search?.main?.filters);
    const currentQueryString = useSelector((state) => state.search?.main?.queryString);

    const country = homes?.[0]?.country() || null;

    const lastDispatchedLocationKey = useRef(null);
    const [locationKey, setLocationKey] = useState(() =>
        currentFilters?.location?.bounds && !currentFilters?.location?.polygon?.location_id
            ? 'bounds'
            : currentFilters?.location?.polygon?.location_id || 'none'
    );

    const { data: recentSearchesLegacy } = usePromise({
        enabled: Boolean(userId),
        initialData: [],
        promiseFn: fetchRecentSearches
    });

    const recentSearches = useMemo(
        () => transformSearchesToRecentSearches(recentSearchesLegacy),
        [recentSearchesLegacy]
    );

    useEffect(() => {
        const nextKey =
            currentFilters?.location?.bounds && !currentFilters?.location?.polygon?.location_id
                ? 'bounds'
                : currentFilters?.location?.polygon?.location_id || 'none';
        if (lastDispatchedLocationKey.current !== null && nextKey === lastDispatchedLocationKey.current) {
            return;
        }
        lastDispatchedLocationKey.current = null;
        if (nextKey !== locationKey) {
            setLocationKey(nextKey);
        }
    }, [currentFilters, locationKey]);

    const searchBarProps = isOnSearchPage
        ? {
              showSearchButton: true,
              autoAdvance: { desktop: false },
              defaultValue: getSearchBarDefaultValue(currentFilters, currentQueryString),
              onSearch: () => {
                  const currentPage = parseInt(
                      new URLSearchParams(window.location.search).get('page') || '1',
                      10
                  );
                  if (currentPage > 1) {
                      document.dispatchEvent(
                          new CustomEvent('update_page', {
                              detail: { page: 1, forceSearch: true }
                          })
                      );
                  } else {
                      document.dispatchEvent(new CustomEvent('search-start', { detail: { type: 'main' } }));
                      setTimeout(() => {
                          document.dispatchEvent(
                              new CustomEvent('search-success', { detail: { type: 'main' } })
                          );
                      }, 800);
                  }
              },
              onChange: (state) => {
                  const { location, dateResult, guests, quickFilters } = state;
                  const queryString = location && location.id !== 'everywhere' ? location.value : '';
                  const filters = {};

                  if (location && location.id !== 'everywhere') {
                      if (location.bounds) {
                          filters.location = { bounds: location.bounds };
                      } else {
                          filters.location = {
                              polygon: {
                                  location_id: location.id,
                                  provider: 'Jawg',
                                  ...(location.layer && { layer: location.layer })
                              },
                              ...(location.city && { city: location.city }),
                              ...(location.country_code && { country_code: location.country_code })
                          };
                      }
                  }
                  if (dateResult?.activeTabIndex === 2) {
                      filters.calendar = { last_minute: true };
                  } else if (
                      dateResult?.activeTabIndex === 1 &&
                      dateResult?.flexibleDuration &&
                      dateResult?.selectedMonths?.length
                  ) {
                      const formatMonthRange = (d) => {
                          const date = new Date(d);
                          const y = date.getFullYear();
                          const monthIndex = date.getMonth();
                          const m = String(monthIndex + 1).padStart(2, '0');
                          const lastDay = String(new Date(y, monthIndex + 1, 0).getDate()).padStart(2, '0');
                          return { from: `${y}-${m}-01`, to: `${y}-${m}-${lastDay}` };
                      };
                      filters.calendar = {
                          date_ranges: dateResult.selectedMonths.map((m) => formatMonthRange(m)),
                          duration: { kind: dateResult.flexibleDuration }
                      };
                  } else if (dateResult?.activeTabIndex === 0 && dateResult?.dateRange) {
                      const formatDate = (d) => {
                          const date = new Date(d);
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          return `${y}-${m}-${day}`;
                      };
                      const flexibilityMap = { '1day': 1, '3days': 3, '7days': 7, '30days': 30 };
                      const flexibilityValue = flexibilityMap[dateResult.flexibility];
                      filters.calendar = {
                          date_ranges: [
                              {
                                  from: formatDate(dateResult.dateRange.start),
                                  to: formatDate(dateResult.dateRange.end)
                              }
                          ],
                          ...(flexibilityValue ? { flexibility: flexibilityValue } : {})
                      };
                  }

                  const quickAmenities = [];
                  const quickSurrounding = [];
                  const homeFilters = {};
                  let luxury;
                  if (quickFilters?.length) {
                      quickFilters.forEach((f) => {
                          if (f === 'home' || f === 'flat') {
                              homeFilters.type = f;
                          } else if (f === 'primary') {
                              homeFilters.is_primary = true;
                          } else if (f === 'private-room') {
                              homeFilters.is_private_room = true;
                          } else if (f === 'eco-level') {
                              homeFilters.eco_level = true;
                          } else if (f === 'luxury') {
                              luxury = true;
                          } else if (surroundingTags.includes(f)) {
                              quickSurrounding.push(f);
                          } else {
                              quickAmenities.push(f);
                          }
                      });
                  }

                  const preservedHome = currentFilters?.home || {};
                  filters.home = {
                      ...preservedHome,
                      size: {
                          ...preservedHome.size,
                          beds: {
                              adults: guests.adults || 0,
                              children: guests.children || 0,
                              babies: guests.babies || 0
                          }
                      },
                      amenities: _.union(preservedHome.amenities || [], quickAmenities),
                      surrounding: _.union(preservedHome.surrounding || [], quickSurrounding),
                      ...homeFilters
                  };

                  const mergedCalendar = filters.calendar
                      ? {
                            ...filters.calendar,
                            ...(currentFilters?.calendar?.exchange_types?.length
                                ? { exchange_types: currentFilters.calendar.exchange_types }
                                : {})
                        }
                      : currentFilters?.calendar?.exchange_types?.length
                      ? { exchange_types: currentFilters.calendar.exchange_types }
                      : undefined;

                  const preserved = {};
                  [
                      'guestpoints',
                      'filters',
                      'favorite',
                      'reverse',
                      'preferred_destination',
                      'points_of_interest'
                  ].forEach((key) => {
                      if (currentFilters?.[key] !== undefined) {
                          preserved[key] = currentFilters[key];
                      }
                  });
                  if (currentFilters?.host?.groups?.length) {
                      preserved.host = { groups: currentFilters.host.groups };
                  }

                  const mergedFilters = {
                      ...preserved,
                      ...(filters.location ? { location: filters.location } : {}),
                      home: filters.home,
                      ...(mergedCalendar ? { calendar: mergedCalendar } : {}),
                      ...(luxury ? { luxury: true } : {})
                  };

                  if (!location || location.id === 'everywhere') {
                      lastDispatchedLocationKey.current = 'none';
                  } else if (location.bounds) {
                      lastDispatchedLocationKey.current = 'bounds';
                  } else {
                      lastDispatchedLocationKey.current = location.id;
                  }
                  dispatch(searchProcessing({ queryString, filters: mergedFilters, placeId: location?.id }));
                  document.dispatchEvent(new CustomEvent('update_page', { detail: { page: 1 } }));
              }
          }
        : {
              onSearch: (searchUrl) => {
                  window.location.href = searchUrl;
              }
          };

    return (
        <CompleteSearchBar
            key={locationKey}
            variant="compact"
            config={{
                apiUrl: process.env.URL_BFF,
                assetsUrl: process.env.URL_MIGRATION_ASSETS,
                jawgToken: process.env.JAWG_KEY,
                jawgUrl: process.env.URL_JAWG_URL,
                locale,
                recentSearches,
                country
            }}
            {...searchBarProps}
        />
    );
};

export default NavSearchBar;
